import React, { useState, useRef } from 'react';
import { Upload, ImagePlus, X, Sparkles, ArrowRight, Ruler, Palette, MessageCircle } from 'lucide-react';

type ProductType = 'poster' | 'sticker';
type PosterSize = 'A5' | 'A4' | 'A3' | '13x19';

interface SizeOption {
  id: PosterSize;
  label: string;
  dimensions: string;
  price: string;
}

const posterSizes: SizeOption[] = [
  { id: 'A5', label: 'A5', dimensions: '5.8 × 8.3 in', price: '₹79' },
  { id: 'A4', label: 'A4', dimensions: '8.3 × 11.7 in', price: '₹129' },
  { id: 'A3', label: 'A3', dimensions: '11.7 × 16.5 in', price: '₹199' },
  { id: '13x19', label: '13×19"', dimensions: '13 × 19 in', price: '₹249' },
];

const CustomPosterSection: React.FC = () => {
  const [productType, setProductType] = useState<ProductType>('poster');
  const [selectedSize, setSelectedSize] = useState<PosterSize>('A4');
  const [uploadedImages, setUploadedImages] = useState<{ file: File; preview: string }[]>([]);
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: { file: File; preview: string }[] = [];
    for (let i = 0; i < files.length; i++) {
      if (uploadedImages.length + newImages.length >= 5) break;
      const file = files[i];
      if (file.size > 50 * 1024 * 1024) continue; // 50MB max
      newImages.push({
        file,
        preview: URL.createObjectURL(file),
      });
    }
    setUploadedImages(prev => [...prev, ...newImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = () => {
    // Build WhatsApp message
    const sizeInfo = posterSizes.find(s => s.id === selectedSize);
    const message = encodeURIComponent(
      `🎨 Custom ${productType === 'poster' ? 'Poster' : 'Sticker'} Request\n\n` +
      `📏 Size: ${sizeInfo?.label} (${sizeInfo?.dimensions})\n` +
      `💰 Starting at: ${sizeInfo?.price}\n` +
      `📝 Notes: ${notes || 'None'}\n` +
      `🖼️ Selected Images: ${uploadedImages.length}\n\n` +
      `I'd like to place a custom order! I will attach my high-resolution image(s) below.`
    );
    window.open(`https://wa.me/917896409312?text=${message}`, '_blank'); // Replace with actual number
  };

  return (
    <section id="customize" className="py-24 bg-neutral-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-amber-900/10 via-transparent to-transparent rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-pink-900/10 via-transparent to-transparent rounded-full blur-[100px] pointer-events-none"></div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <h2 className="text-xs font-bold tracking-[0.4em] text-amber-500 mb-4 uppercase">Your Vision, Our Craft</h2>
          <p className="text-3xl md:text-5xl font-black tracking-tighter uppercase mb-4 leading-tight">
            Design Your 
            <span className="text-amber-500"> Own</span>
          </p>
          <p className="text-neutral-400 text-sm md:text-base leading-relaxed max-w-xl">
            Upload your favorite image and we'll turn it into a premium poster or sticker. 
            300 GSM matte finish, vibrant colors, built to last — your dream artwork, printed and shipped to your door.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">

          {/* ─── Left: Upload & Preview Area ─── */}
          <div className="space-y-6">
            {/* Product Type Toggle */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-3 block flex items-center gap-2">
                <Palette className="w-3.5 h-3.5" /> Product Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setProductType('poster')}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    productType === 'poster'
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-white/10 bg-neutral-800/50 hover:border-white/20'
                  }`}
                >
                  <span className="text-xs font-black uppercase tracking-widest block mb-1">Poster</span>
                  <span className="text-[10px] text-neutral-400">300 GSM Matte Board</span>
                </button>
                <button
                  onClick={() => setProductType('sticker')}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    productType === 'sticker'
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-white/10 bg-neutral-800/50 hover:border-white/20'
                  }`}
                >
                  <span className="text-xs font-black uppercase tracking-widest block mb-1">Sticker</span>
                  <span className="text-[10px] text-neutral-400">Vinyl Die-Cut</span>
                </button>
              </div>
            </div>

            {/* Size Selector (poster only) */}
            {productType === 'poster' && (
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-3 block flex items-center gap-2">
                  <Ruler className="w-3.5 h-3.5" /> Select Size
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {posterSizes.map(size => (
                    <button
                      key={size.id}
                      onClick={() => setSelectedSize(size.id)}
                      className={`p-3 rounded-xl border-2 transition-all duration-300 text-center ${
                        selectedSize === size.id
                          ? 'border-amber-500 bg-amber-500/10'
                          : 'border-white/10 bg-neutral-800/50 hover:border-white/20'
                      }`}
                    >
                      <span className="text-xs font-black block">{size.label}</span>
                      <span className="text-[9px] text-neutral-500 block mt-0.5">{size.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Image Upload Area */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-3 block flex items-center gap-2">
                <ImagePlus className="w-3.5 h-3.5" /> Upload Your Image
              </label>

              {/* Upload Zone */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="group relative border-2 border-dashed border-white/10 hover:border-amber-500/40 rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 hover:bg-amber-500/5"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="w-8 h-8 text-neutral-500 group-hover:text-amber-400 mx-auto mb-3 transition-colors duration-300" />
                <p className="text-xs font-bold text-neutral-400 group-hover:text-white transition-colors mb-1">
                  Drop your images here or click to browse
                </p>
                <p className="text-[10px] text-neutral-600">
                  JPG, PNG, WEBP • Max 50MB • Up to 5 images
                </p>
              </div>

              {/* Uploaded Previews */}
              {uploadedImages.length > 0 && (
                <div className="flex gap-3 mt-4 overflow-x-auto no-scrollbar pb-2">
                  {uploadedImages.map((img, i) => (
                    <div key={i} className="relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-white/10 group">
                      <img src={img.preview} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-3 block">
                Additional Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions? Color adjustments, text overlay, border style..."
                className="w-full bg-neutral-800/50 border border-white/10 rounded-xl p-4 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500 outline-none min-h-[80px] resize-none transition-colors"
              />
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-amber-600 to-pink-600 hover:from-amber-500 hover:to-pink-500 text-white py-5 rounded-2xl font-black uppercase tracking-[0.15em] transition-all duration-300 flex items-center justify-center gap-3 group shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30"
            >
              <MessageCircle className="w-5 h-5" />
              <span>Send Custom Request via WhatsApp</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <div className="text-center space-y-1 mt-3">
              <p className="text-xs font-bold text-amber-500">
                ⚠️ Important: Please attach your images in the WhatsApp chat!
              </p>
              <p className="text-[10px] text-neutral-600">
                To maintain print quality, we accept high-resolution files directly via WhatsApp.
              </p>
            </div>
          </div>

          {/* ─── Right: How It Works ─── */}
          <div className="space-y-6 lg:pt-4">
            <h3 className="text-xs font-black uppercase tracking-widest text-neutral-500 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" /> How It Works
            </h3>

            {/* Steps */}
            <div className="space-y-0">
              {[
                {
                  step: '01',
                  title: 'Upload Your Image',
                  description: 'Choose your favorite photo, artwork, or design. High resolution gives the best results.',
                },
                {
                  step: '02',
                  title: 'Pick Size & Type',
                  description: 'Select from our poster sizes or choose a custom sticker/skin. We support A5 all the way to 13×19".',
                },
                {
                  step: '03',
                  title: 'We Review & Print',
                  description: 'Our team reviews your submission for quality. Printed on premium 300 GSM matte board with vibrant, fade-resistant inks.',
                },
                {
                  step: '04',
                  title: 'Shipped to Your Door',
                  description: 'Carefully packed and shipped across India. Your custom piece arrives ready to transform your space.',
                },
              ].map((item, index) => (
                <div key={index} className="group relative flex gap-5 p-5 rounded-xl hover:bg-neutral-800/30 transition-colors duration-300">
                  {/* Step number */}
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                      <span className="text-xs font-black text-amber-400">{item.step}</span>
                    </div>
                    {/* Connector line */}
                    {index < 3 && (
                      <div className="w-px h-6 bg-white/5 mx-auto mt-2"></div>
                    )}
                  </div>
                  {/* Content */}
                  <div className="pt-1">
                    <h4 className="text-white font-bold text-sm uppercase tracking-wide mb-1">{item.title}</h4>
                    <p className="text-neutral-500 text-xs leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quality Badges */}
            <div className="grid grid-cols-3 gap-3 pt-4">
              {[
                { label: '300 GSM', subtitle: 'Premium Board' },
                { label: 'Fade-Proof', subtitle: 'Vivid Colors' },
                { label: 'All India', subtitle: 'Free Shipping*' },
              ].map((badge, i) => (
                <div key={i} className="text-center p-3 rounded-xl bg-neutral-800/50 border border-white/5">
                  <span className="text-xs font-black text-white block">{badge.label}</span>
                  <span className="text-[9px] text-neutral-500">{badge.subtitle}</span>
                </div>
              ))}
            </div>

            {/* Important Notice */}
            <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-4">
              <p className="text-[10px] text-amber-300/70 leading-relaxed">
                <span className="font-black text-amber-300">Note:</span> Uploaded images undergo review. 
                We reserve the right to reject content that violates our guidelines. 
                Colors may vary slightly from screen to print due to display differences — this is normal and not a defect.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default CustomPosterSection;
